import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import PostContentRenderer from './PostContentRenderer';
import '../styles/PostContent.css';

interface Like {
  user_id: string;
  created_at: string;
}

interface PostCardProps {
  postId: string;
  photo: string;
  name: string;
  content: string;
  category: string;
  likes: Like[];
  currentUserId?: string;
  createdAt?: string;
}

const PostCard: React.FC<PostCardProps> = ({
  postId,
  photo,
  name,
  content,
  category,
  likes: initialLikes,
  currentUserId,
  createdAt
}) => {
  const [likes, setLikes] = useState<Like[]>(initialLikes);
  const [isLiking, setIsLiking] = useState(false);

  // Check if current user has liked the post
  const isLiked = currentUserId ? likes.some(like => like.user_id === currentUserId) : false;

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3030';

  const getTokenFromCookies = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'sgo_access_token') {
        return value;
      }
    }
    return null;
  };

  const handleLikeToggle = async () => {
    if (isLiking) return; // Evita múltiplos cliques

    setIsLiking(true);
    const token = getTokenFromCookies();

    if (!token) {
      alert('Você precisa estar logado para curtir posts');
      setIsLiking(false);
      return;
    }

    try {
      const method = isLiked ? 'DELETE' : 'POST';
      const response = await fetch(`${API_BASE_URL}/posts/${postId}/like`, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        if (isLiked) {
          // Remove current user's like
          setLikes(prev => prev.filter(like => like.user_id !== currentUserId));
        } else {
          // Add current user's like
          const newLike: Like = {
            user_id: currentUserId!,
            created_at: new Date().toISOString()
          };
          setLikes(prev => [...prev, newLike]);
        }
      } else {
        const errorData = await response.text();
        console.error('Erro ao curtir/descurtir post:', errorData);
      }
    } catch (error) {
      console.error('Erro ao curtir/descurtir post:', error);
    } finally {
      setIsLiking(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200">
      <div className="flex items-center p-4 border-b border-gray-100">
        <img
          src={photo}
          alt={name}
          className="w-12 h-12 rounded-full object-cover mr-3"
        />
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900">{name}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className="inline-block bg-gold-100 text-gold-800 text-xs px-2 py-1 rounded-full">
              {category}
            </span>
            {createdAt && (
              <span className="text-xs text-gray-500">
                {formatDate(createdAt)}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="mb-4">
          <PostContentRenderer content={content} />
        </div>

        <div className="flex items-center justify-between border-t border-gray-100 pt-3">
          <button
            onClick={handleLikeToggle}
            disabled={isLiking}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors duration-200 disabled:opacity-50 ${
              isLiked
                ? 'bg-red-50 text-red-600 hover:bg-red-100'
                : 'text-gray-500 hover:bg-gray-50 hover:text-red-600'
            }`}
          >
            <Heart
              size={20}
              className={`transition-all duration-200 ${
                isLiked ? 'fill-current text-red-600' : ''
              } ${isLiking ? 'animate-pulse' : ''}`}
            />
            <span className="text-sm font-medium">{likes.length}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostCard;